import axios from 'axios'
import React, { useEffect, useState } from 'react'
import { Card, Spinner, Table, Badge, Pagination, Dropdown, Nav } from 'react-bootstrap'
import { useHistory, useLocation, Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faPlus, faEdit, faTrash, faCalendarAlt } from '@fortawesome/free-solid-svg-icons'
import ReactPlayer from 'react-player'
import {
    VendorAdGetEnd,
    VendorAdDeleteEnd,
    VendorProfileEnd
} from '../../constants/api.constants'
import Toast from '../../utils/Toast/Toast'
import VendorAddAdModal from '../../components/Modals/VendorAddAdModal/VendorAddAdModal'
import VendorEditAdModal from '../../components/Modals/VendorAddAdModal/VendorEditAdModal'
import VendorScheduleAdModal from '../../components/Modals/VendorScheduleAdModal/VendorScheduleAdModal'
import './VendorAdManagement.scss'

const VendorAdManagement = () => {
    const history = useHistory()
    const location = useLocation()
    const [ads, setAds] = useState([])
    const [vendorInfo, setVendorInfo] = useState({})
    const [spinner, setSpinner] = useState(true)
    const [searchKey, setSearchKey] = useState('')
    const [page, setPage] = useState(1)
    const [totalDoc, setTotalDoc] = useState(0)

    // Modals
    const [showAddModal, setShowAddModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [showScheduleModal, setShowScheduleModal] = useState(false)
    const [selectedAd, setSelectedAd] = useState(null)
    const [deleteSpinner, setDeleteSpinner] = useState(null)

    useEffect(() => {
        const token = localStorage.getItem('vendor_token')
        if (!token) {
            history.push('/login')
            return
        }
        loadVendorProfile()
    }, [])

    useEffect(() => {
        loadAds()
    }, [page, searchKey])

    const loadVendorProfile = async () => {
        try {
            const response = await axios.get(VendorProfileEnd, {
                headers: {
                    menuboard: localStorage.getItem('vendor_token'),
                },
            })
            if (response.status === 200) {
                setVendorInfo(response.data.data?.data || {})
            }
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                Toast('err', 'Session expired. Please login again.')
                handleLogout()
            }
        }
    }

    const loadAds = async () => {
        setSpinner(true)
        try {
            let url = VendorAdGetEnd + `?page=${page}`
            if (searchKey) {
                url += `&name=${searchKey}`
            }

            const response = await axios.get(url, {
                headers: {
                    menuboard: localStorage.getItem('vendor_token'),
                },
            })

            if (response.status === 200) {
                setAds(response.data.data || [])
                setTotalDoc(response.data.total_document || 0)
            }
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                Toast('err', 'Session expired. Please login again.')
                handleLogout()
            } else {
                Toast('err', error.response?.data?.msg || 'Failed to load ads')
            }
        }
        setSpinner(false)
    }

    const handleDeleteAd = async (adId) => {
        if (!window.confirm('Are you sure you want to delete this ad?')) return

        setDeleteSpinner(adId)
        try {
            const response = await axios.delete(VendorAdDeleteEnd + `?_id=${adId}`, {
                headers: {
                    menuboard: localStorage.getItem('vendor_token'),
                },
            })

            if (response.status === 200) {
                Toast('success', 'Ad deleted successfully')
                loadAds()
            }
        } catch (error) {
            Toast('err', error.response?.data?.msg || 'Failed to delete ad')
        }
        setDeleteSpinner(null)
    }

    const handleEditAd = (ad) => {
        setSelectedAd(ad)
        setShowEditModal(true)
    }

    const handleLogout = () => {
        localStorage.removeItem('vendor_token')
        localStorage.removeItem('vendor_info')
        localStorage.removeItem('user_role')
        localStorage.setItem('default_path', '/')
        history.push('/login')
    }

    const totalPage = Math.ceil(totalDoc / 10)
    const startIndex = (page - 1) * 10 + 1

    return (
        <div className='vendor-ad-management py-4'>
            {/* Vendor Sidebar Navigation */}
            <Card className='mb-4'>
                <Card.Body className='py-2'>
                    <Nav variant='pills' className='vendor-nav'>
                        <Nav.Item>
                            <Nav.Link
                                as={Link}
                                to='/vendor-dashboard'
                                active={location.pathname === '/vendor-dashboard'}
                            >
                                Dashboard
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                as={Link}
                                to='/vendor-ad-management'
                                active={location.pathname === '/vendor-ad-management'}
                            >
                                Ad Management
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item className='ms-auto'>
                            <Nav.Link onClick={handleLogout} className='text-danger'>
                                Logout
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </Card.Body>
            </Card>

            <div className='d-flex justify-content-between align-items-center mb-4'>
                <div>
                    <h3 className='fw-bold mb-1'>Ad Management</h3>
                    <p className='text-muted mb-0'>
                        Welcome, {vendorInfo.name || 'Vendor'}
                    </p>
                </div>
                <div className='d-flex gap-2'>
                    <button
                        className='btn btn-success d-flex align-items-center'
                        onClick={() => setShowScheduleModal(true)}
                    >
                        <FontAwesomeIcon icon={faCalendarAlt} className='me-2' />
                        Schedule Ads on Screens
                    </button>
                    <button
                        className='btn btn-primary d-flex align-items-center'
                        onClick={() => setShowAddModal(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} className='me-2' />
                        Upload New Ad
                    </button>
                </div>
            </div>

            <Card className='mb-4'>
                <Card.Header className='d-flex justify-content-between align-items-center'>
                    <h5 className='mb-0'>My Ads</h5>
                    <div className='search-box' style={{ position: 'relative', width: '300px' }}>
                        <input
                            type='text'
                            className='form-control'
                            placeholder='Search by name...'
                            value={searchKey}
                            onChange={(e) => {
                                setSearchKey(e.target.value)
                                setPage(1)
                            }}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            style={{
                                position: 'absolute',
                                right: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#999'
                            }}
                        />
                    </div>
                </Card.Header>
                <Card.Body>
                    {spinner ? (
                        <div className='text-center py-5'>
                            <Spinner animation='border' style={{ color: '#558f55' }} />
                        </div>
                    ) : ads.length > 0 ? (
                        <>
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Preview</th>
                                        <th>Name</th>
                                        <th>Description</th>
                                        <th>Type</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ads.map((ad, idx) => (
                                        <tr key={ad._id}>
                                            <td>{startIndex + idx}</td>
                                            <td style={{ width: '150px' }}>
                                                {ad.type === 'photo' ? (
                                                    <img
                                                        src={ad.link}
                                                        alt={ad.name}
                                                        style={{
                                                            width: '100px',
                                                            height: '60px',
                                                            objectFit: 'cover',
                                                            borderRadius: '4px'
                                                        }}
                                                    />
                                                ) : (
                                                    <ReactPlayer
                                                        url={ad.link}
                                                        width='100px'
                                                        height='60px'
                                                        controls={false}
                                                    />
                                                )}
                                            </td>
                                            <td>{ad.name}</td>
                                            <td>{ad.description || '-'}</td>
                                            <td>
                                                <Badge bg={ad.type === 'photo' ? 'info' : 'warning'}>
                                                    {ad.type}
                                                </Badge>
                                            </td>
                                            <td>
                                                {new Date(ad.createdAt).toLocaleDateString()}
                                            </td>
                                            <td>
                                                <button
                                                    className='btn btn-sm btn-outline-primary me-2'
                                                    onClick={() => handleEditAd(ad)}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button
                                                    className='btn btn-sm btn-outline-danger'
                                                    onClick={() => handleDeleteAd(ad._id)}
                                                    disabled={deleteSpinner === ad._id}
                                                >
                                                    {deleteSpinner === ad._id ? (
                                                        <Spinner animation='border' size='sm' />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            {totalPage > 1 && (
                                <div className='d-flex justify-content-center'>
                                    <Pagination>
                                        {[...Array(totalPage)].map((_, index) => (
                                            <Pagination.Item
                                                key={index}
                                                active={index + 1 === page}
                                                onClick={() => setPage(index + 1)}
                                            >
                                                {index + 1}
                                            </Pagination.Item>
                                        ))}
                                    </Pagination>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className='text-center py-5'>
                            <h5 className='text-muted'>No ads found</h5>
                            <p className='text-muted'>
                                Click "Upload New Ad" to create your first advertisement.
                            </p>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Add Ad Modal */}
            <VendorAddAdModal
                show={showAddModal}
                handleClose={() => setShowAddModal(false)}
                loadAds={loadAds}
            />

            {/* Edit Ad Modal */}
            {selectedAd && (
                <VendorEditAdModal
                    show={showEditModal}
                    handleClose={() => {
                        setShowEditModal(false)
                        setSelectedAd(null)
                    }}
                    ad={selectedAd}
                    loadAds={loadAds}
                />
            )}

            {/* Schedule Ad Modal */}
            <VendorScheduleAdModal
                show={showScheduleModal}
                handleClose={() => setShowScheduleModal(false)}
            />
        </div>
    )
}

export default VendorAdManagement
